import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Blog: React.FC = () => {
  const blogPosts = [
    {
      id: 1,
      title: 'Best Fishing Spots in the UK',
      date: '2024-02-15',
      author: 'David Wilson',
      category: 'Fishing Tips',
      image: '/images/fishingboat1.jpg',
      excerpt: 'Discover the top fishing locations across the United Kingdom, from the serene lochs of Scotland to the bustling rivers of England. Learn about the best times to visit and what species you can expect to catch.',
      content: 'Scotland\'s lochs offer some of the most spectacular fishing experiences in the UK. Loch Lomond, in particular, is renowned for its diverse fish population and stunning scenery. The best time to visit is during the spring and autumn months when the water temperatures are ideal for fishing. You can expect to catch pike, perch, and trout, with the occasional salmon making an appearance during the right season.',
      readTime: '5 min read'
    },
    {
      id: 2,
      title: 'Essential Fishing Gear Guide',
      date: '2024-02-10',
      author: 'Emma Thompson',
      category: 'Equipment',
      image: '/images/fishing2.jpg',
      excerpt: 'A comprehensive guide to the must-have equipment for every angler, from beginners to professionals. Learn about the essential gear that will enhance your fishing experience.',
      content: 'Having the right equipment can make all the difference in your fishing success. Start with a quality rod and reel combination that matches your fishing style. For beginners, a medium-action rod is versatile enough for most situations. Don\'t forget essential accessories like fishing line, hooks, and bait. Quality gear doesn\'t have to break the bank, but investing in reliable equipment will pay off in the long run.',
      readTime: '7 min read'
    },
    {
      id: 3,
      title: 'Fishing Techniques for Beginners',
      date: '2024-02-05',
      author: 'James Anderson',
      category: 'Tutorials',
      image: '/images/fishing3.jpg',
      excerpt: 'Learn the fundamental techniques to get started with fishing. From casting to reeling, we cover everything you need to know to begin your fishing journey.',
      content: 'Mastering basic fishing techniques is crucial for beginners. Start with the overhead cast, which is the most common casting method. Practice your timing and aim in an open area before hitting the water. Learn to read the water for signs of fish activity, such as ripples or jumping fish. Remember, patience is key in fishing - sometimes the best technique is simply waiting for the fish to come to you.',
      readTime: '6 min read'
    },
    {
      id: 4,
      title: 'Sustainable Fishing Practices',
      date: '2024-01-28',
      author: 'Sarah Johnson',
      category: 'Conservation',
      image: '/images/scenery2.jpg',
      excerpt: 'Explore eco-friendly fishing practices that help preserve our waterways and fish populations for future generations.',
      content: 'Sustainable fishing is essential for maintaining healthy fish populations and ecosystems. Practice catch and release when possible, using barbless hooks to minimize harm to fish. Be mindful of fishing seasons and size limits, which are designed to protect breeding populations. Consider using biodegradable fishing line and lead-free weights to reduce environmental impact.',
      readTime: '8 min read'
    },
    {
      id: 5,
      title: 'Fishing in Different Seasons',
      date: '2024-01-20',
      author: 'Michael Brown',
      category: 'Seasonal Guide',
      image: '/images/scenery5.jpg',
      excerpt: 'A seasonal guide to fishing, helping you understand how weather and water conditions affect fish behavior throughout the year.',
      content: 'Each season brings unique fishing opportunities and challenges. Spring is ideal for trout fishing as they become more active in warming waters. Summer offers the best conditions for night fishing, while autumn is perfect for catching larger fish as they feed heavily before winter. Winter fishing requires special techniques and equipment, but can be incredibly rewarding for those willing to brave the cold.',
      readTime: '9 min read'
    },
    {
      id: 6,
      title: 'Fishing Photography Tips',
      date: '2024-01-15',
      author: 'Lisa Anderson',
      category: 'Photography',
      image: '/images/fishing7.jpg',
      excerpt: 'Capture your fishing adventures with these professional photography tips. Learn how to take stunning photos of your catches and scenic fishing spots.',
      content: 'Great fishing photography combines technical skill with an eye for composition. Use natural light to your advantage, with early morning and late afternoon providing the best conditions. When photographing your catch, keep the fish wet and handle it gently. Include the surrounding scenery to tell a complete story of your fishing adventure.',
      readTime: '6 min read'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Blog - Anchor Management</title>
        <meta name="description" content="Read the latest fishing tips, stories, and updates from Anchor Management at Loch Lomond" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/scenery 1.jpg"
            alt="Loch Lomond fishing scene"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-4"
          >
            Fishing Blog
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-2xl mx-auto"
          >
            Stories, tips, and insights from our fishing adventures
          </motion.p>
        </div>
        
        {/* Wave Shape */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            className="w-full h-auto"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#00B4D8"
              fillOpacity="0.8"
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="relative h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-primary-600 text-white px-3 py-1 rounded-full text-sm">
                    {post.category}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <span>{post.author}</span>
                    <span className="mx-2">•</span>
                    <time>{new Date(post.date).toLocaleDateString('en-GB')}</time>
                    <span className="mx-2">•</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h2 className="text-xl font-semibold mb-3 text-gray-800 dark:text-white">{post.title}</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">{post.excerpt}</p>
                  <Link
                    to={`/blog/${post.id}`}
                    className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    Read More →
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8 text-gray-800 dark:text-white">Subscribe to Our Newsletter</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Stay updated with our latest fishing tips, stories, and exclusive offers
          </p>
          <div className="max-w-md mx-auto">
            <div className="flex gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-full border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-600 dark:bg-gray-800 dark:text-white"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-700 transition-colors"
              >
                Subscribe
              </motion.button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog; 